// WHERE a tooltip goes, as arithmetic — no DOM, so the rule can be reasoned about and tested on its
// own (the directive in lib/directives/Tooltip does the measuring and the moving).
//
// The tooltip used to be a pair of CSS pseudo-elements on the anchor itself, which meant any
// scroller or `overflow: hidden` above it cut the bubble in half — the global timeline's overdue
// bell sits at the left edge of the side panel and asks for a tooltip on its LEFT, so half of it
// disappeared under the neighbouring section (#85). A bubble drawn on <body> in fixed coordinates
// cannot be clipped by an ancestor; all that is left to decide is where in the VIEWPORT it lands,
// which is this file.
//
// Two rules, in order:
//   1. FLIP — try the asked-for side, then its opposite, then the other axis. The first side the
//      bubble fits on wins.
//   2. SHIFT — whatever side won, slide the bubble back inside the viewport (never closer than
//      `margin` to an edge). A tooltip longer than the viewport is pinned to the left/top margin
//      rather than being pushed off the far edge.

// The sides to try, in order, for each asked-for position. The opposite side comes first because a
// tooltip that flips over its anchor still points at the same thing; hopping to the other axis is
// the last resort.
const FALLBACKS = {
  top: ['top', 'bottom', 'right', 'left'],
  bottom: ['bottom', 'top', 'right', 'left'],
  left: ['left', 'right', 'top', 'bottom'],
  right: ['right', 'left', 'top', 'bottom'],
}

// In a LIST the bubble may only go above or below (busy-my-web#385). The reason is functional: a
// row's action icons sit in a line, so a bubble beside one of them covers the others and the user
// cannot see what else the row offers. Fatemeh's rule is "above the icon, or below it when there is
// no room above" — so a list anchor's sides are the vertical pair and nothing else.
const VERTICAL_FALLBACKS = {
  top: ['top', 'bottom'],
  bottom: ['bottom', 'top'],
  left: ['top', 'bottom'],
  right: ['top', 'bottom'],
}

// The requested side, from the directive's argument ("left", "top-start", "" …). Anything the app
// doesn't draw reads as "top", which is the directive's own default.
export function sideOf(position) {
  const side = String(position || '').split('-')[0].toLowerCase()
  return FALLBACKS[side] ? side : 'top'
}

// Anything a list row asked for on the horizontal axis becomes "above"; "bottom" is left alone,
// because a call site that deliberately hangs its bubble under an icon is still above/below.
export function listSide(position) {
  const side = sideOf(position)
  return side === 'bottom' ? 'bottom' : 'top'
}

// The sides to try, in order. `vertical` is the list rule; without it the old flip chain stands, so
// nothing outside a list moves.
export function sidesFor(position, vertical = false) {
  return vertical ? VERTICAL_FALLBACKS[sideOf(position)] : FALLBACKS[sideOf(position)]
}

// Where the bubble's top-left corner sits for a given side — centred on the anchor along the side's
// free axis, `gap` away from it along the other.
function cornerFor(side, anchor, tip, gap) {
  const midX = anchor.left + anchor.width / 2 - tip.width / 2
  const midY = anchor.top + anchor.height / 2 - tip.height / 2
  if (side === 'bottom') return { left: midX, top: anchor.top + anchor.height + gap }
  if (side === 'left') return { left: anchor.left - tip.width - gap, top: midY }
  if (side === 'right') return { left: anchor.left + anchor.width + gap, top: midY }
  return { left: midX, top: anchor.top - tip.height - gap }
}

function fitsIn(corner, tip, viewport, margin) {
  return corner.left >= margin
    && corner.top >= margin
    && corner.left + tip.width <= viewport.width - margin
    && corner.top + tip.height <= viewport.height - margin
}

// Keep a value inside [min, max]; a bubble bigger than the space available takes `min`, so it is the
// far edge that overflows and the start of the text that stays readable.
function clamp(value, min, max) {
  if (max < min) return min
  return Math.min(Math.max(value, min), max)
}

/**
 * @param {object}  o
 * @param {DOMRect} o.anchor    the element the tooltip belongs to, in viewport coordinates
 * @param {object}  o.tip       the bubble's own { width, height }
 * @param {object}  o.viewport  { width, height }
 * @param {string} [o.position] the side asked for ("top" | "bottom" | "left" | "right")
 * @param {number} [o.gap]      space between the anchor and the bubble
 * @param {number} [o.margin]   how close to a viewport edge the bubble may come
 * @param {boolean} [o.vertical] list rule (busy-my-web#385): above, or below — never beside
 * @returns {{ left: number, top: number, placement: string }} fixed-position coordinates
 */
export function placeTooltip({ anchor, tip, viewport, position = 'top', gap = 6, margin = 8, vertical = false }) {
  const sides = sidesFor(position, vertical)
  const wanted = sides[0]
  const placement = sides.find(s => fitsIn(cornerFor(s, anchor, tip, gap), tip, viewport, margin)) || wanted
  const corner = cornerFor(placement, anchor, tip, gap)
  return {
    left: clamp(corner.left, margin, viewport.width - tip.width - margin),
    top: clamp(corner.top, margin, viewport.height - tip.height - margin),
    placement,
  }
}
